/**
 * RelatedPages — sección "También te puede interesar" reutilizable.
 * Uso:
 *   <RelatedPages pages={['sistema-multiagente','seguridad-agentes','soluciones']} />
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const PAGE_META = {
  'sistema-multiagente': {
    label: 'Sistema Multiagente',
    desc: 'Agentes de ventas, agenda, CRM y cobros trabajando en equipo.',
    to: '/sistema-multiagente',
    color: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/20',
    dot: 'bg-cyan-400',
  },
  'seguridad-agentes': {
    label: 'Seguridad de Agentes',
    desc: 'Auth Links, permisos granulares y control de acceso sin código.',
    to: '/seguridad-agentes',
    color: 'from-violet-500/20 to-purple-500/10 border-violet-500/20',
    dot: 'bg-violet-400',
  },
  soluciones: {
    label: 'Soluciones por sector',
    desc: 'Casos adaptados a e-commerce, servicios, salud y agencias.',
    to: '/soluciones',
    color: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/20',
    dot: 'bg-emerald-400',
  },
  recursos: {
    label: 'Recursos y blog',
    desc: 'Artículos técnicos sobre agentes IA, modelos y seguridad.',
    to: '/recursos',
    color: 'from-amber-500/20 to-yellow-500/10 border-amber-500/20',
    dot: 'bg-amber-400',
  },
  'integraciones/deepseek': {
    label: 'Integración DeepSeek',
    desc: 'Inferencia eficiente con DeepSeek R1 a fracción del coste de GPT.',
    to: '/integraciones/deepseek',
    color: 'from-sky-500/20 to-blue-500/10 border-sky-500/20',
    dot: 'bg-sky-400',
  },
  developers: {
    label: 'Developers · MCP',
    desc: 'Conecta tus herramientas a Aynara mediante el protocolo MCP.',
    to: '/developers',
    color: 'from-fuchsia-500/20 to-pink-500/10 border-fuchsia-500/20',
    dot: 'bg-fuchsia-400',
  },
  tendencias: {
    label: 'Tendencias 2026',
    desc: 'Datos verificados de Gartner, Microsoft y Verizon. Hoja de ruta.',
    to: '/tendencias',
    color: 'from-rose-500/20 to-red-500/10 border-rose-500/20',
    dot: 'bg-rose-400',
  },
  'app-movil': {
    label: 'App Móvil Aynara',
    desc: 'Control de agentes desde el móvil. En diseño — lista de espera.',
    to: '/app-movil',
    color: 'from-green-500/20 to-lime-500/10 border-green-500/20',
    dot: 'bg-green-400',
  },
};

export default function RelatedPages({ pages = [], title = 'También te puede interesar' }) {
  const items = pages.map(k => PAGE_META[k]).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 py-16" data-testid="related-pages">
      <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-6">{title}</p>
      <div className={`grid gap-4 ${items.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
        {items.map(item => (
          <Link
            key={item.to}
            to={item.to}
            className={`group flex items-start gap-4 rounded-2xl border bg-gradient-to-br ${item.color} p-5 hover:brightness-110 transition-all duration-200`}
            data-testid={`related-link-${item.to.replace(/\//g,'-').slice(1)}`}
          >
            <div className={`w-2 h-2 rounded-full ${item.dot} shrink-0 mt-1.5`} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-sm group-hover:text-primary transition-colors">{item.label}</p>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">{item.desc}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-0.5" />
          </Link>
        ))}
      </div>
    </section>
  );
}
